<template>
  <ion-page>
    <PageHeaderWithMenu v-show="!currentTest" title="Тестирование" :scrolled="headerScrolled" />
    <div v-if="currentTest" class="testing-take-sticky">
      <div class="liga-float-bar testing-take-float-bar">
        <button type="button" class="liga-float-circle" aria-label="К списку тестов" @click="closeTest">
          <ion-icon :icon="chevronBackOutline" />
        </button>
        <div class="liga-float-title-pill">
          <span class="liga-float-title-text">{{ currentTest.name }}</span>
        </div>
        <div class="liga-float-spacer" aria-hidden="true" />
      </div>
      <div
        v-if="currentTest.description || attemptStartedAt"
        class="testing-take-header"
      >
        <p v-if="currentTest.description" class="testing-take-desc">{{ currentTest.description }}</p>
        <div v-if="attemptStartedAt" class="testing-timer-wrap" :data-tick="timerTick">
          <span class="testing-timer-label">Время: {{ timerElapsed }}</span>
          <span v-if="currentTest.timeLimitMinutes != null" class="testing-timer-remaining">
            {{ timeExpired ? 'Время вышло' : `Осталось: ${timerRemaining}` }}
          </span>
        </div>
      </div>
    </div>
    <ion-content
      :class="[isMobile ? 'main-background' : 'liga-web-background', 'testing-content']"
      :fullscreen="true"
      @ionScroll="onContentScroll">
      <ion-refresher v-if="!currentTest" slot="fixed" @ionRefresh="loadMyTests($event)">
        <ion-refresher-content refreshingSpinner="bubbles"></ion-refresher-content>
      </ion-refresher>

      <!-- Список тестов -->
      <div v-show="!currentTest" class="testing-list-wrap">
        <p v-if="loading" class="testing-hint">Загрузка…</p>
        <p v-else-if="!myTests.length" class="testing-hint">Вам пока не назначены тесты.</p>
        <div v-else class="testing-list">
          <div
            v-for="t in myTests"
            :key="t.id"
            :class="[
              'testing-card',
              {
                'testing-card--passed': t.passed,
                'testing-card--locked': !t.passed && !canOpenTest(t),
              },
            ]"
            @click="onTestCardClick(t)">
            <div class="testing-card-icon">
              <ion-icon :icon="documentTextOutline"></ion-icon>
            </div>
            <div class="testing-card-body">
              <h3 class="testing-card-title">{{ t.name }}</h3>
              <p v-if="t.description" class="testing-card-desc">{{ t.description }}</p>
              <div class="testing-card-meta">
                <span v-if="t.passed" class="testing-status testing-status--passed">
                  <ion-icon :icon="checkmarkCircle"></ion-icon> Пройден
                </span>
                <span v-else-if="!isTestAvailableLocally(t) && !t.hasDraft" class="testing-status testing-status--scheduled">
                  Откроется {{ formatAvailableFrom(t.availableFrom) }}
                  <template v-if="availabilityCountdownSec[t.id] > 0">
                    · {{ formatCountdown(availabilityCountdownSec[t.id]) }}
                  </template>
                </span>
                <span v-else-if="t.attemptsLeft" class="testing-status testing-status--open">Доступен</span>
                <span v-else class="testing-status testing-status--used">Попытки исчерпаны</span>
                <span v-if="t.lastScore != null" class="testing-score">Результат: {{ t.lastScore }}%</span>
              </div>
            </div>
            <ion-icon :icon="chevronForwardOutline" class="testing-card-arrow"></ion-icon>
          </div>
        </div>
      </div>

      <!-- Прохождение теста: скролл только вопросов -->
      <div v-if="currentTest" class="testing-take-wrap">
        <div v-if="testLoading" class="testing-take-loading">
          <ion-spinner name="bubbles"></ion-spinner>
        </div>
        <div v-else class="testing-take-questions">
          <div
            v-for="q in (currentTest.questions || [])"
            :key="q.id"
            class="testing-question-block">
            <p class="testing-question-text">{{ q.text }}</p>
            <div v-if="q.multipleChoice" class="testing-answers">
              <div
                v-for="a in q.answers"
                :key="a.id"
                class="testing-answer-row"
                role="button"
                tabindex="0"
                @click="toggleAnswer(q.id, a.id)"
                @keydown.enter.space.prevent="toggleAnswer(q.id, a.id)">
                <ion-checkbox
                  :checked="isSelected(q.id, a.id)"
                  tabindex="-1"
                  @click.stop
                  @ionChange="onCheckboxChange(q.id, a.id, $event)"></ion-checkbox>
                <span class="testing-answer-label ion-text-wrap">{{ a.text }}</span>
              </div>
            </div>
            <ion-radio-group v-else :value="(selectedAnswers[q.id] || [])[0]" @ionChange="onSingleChange(q.id, $event)" class="testing-answers">
              <div
                v-for="a in (q.answers || [])"
                :key="a.id"
                class="testing-answer-row"
                role="button"
                tabindex="0"
                @click="selectSingleAnswer(q.id, a.id)"
                @keydown.enter.space.prevent="selectSingleAnswer(q.id, a.id)">
                <ion-radio :value="a.id" label-placement="end" :aria-label="a.text" tabindex="-1">
                  <span class="ion-text-wrap testing-answer-label">{{ a.text }}</span>
                </ion-radio>
              </div>
            </ion-radio-group>
          </div>
        </div>
        <div v-if="currentTest && !testLoading" class="testing-take-actions">
          <ion-button expand="block" class="admin-primary-btn" @click="submitTest" :disabled="submitting || timeExpired">
            {{ submitting ? 'Отправка…' : 'Завершить тест' }}
          </ion-button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonRefresher,
  IonRefresherContent,
  IonSpinner,
  toastController,
} from '@ionic/vue';
import {
  checkmarkCircle,
  chevronBackOutline,
  chevronForwardOutline,
  documentTextOutline,
} from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { GlobalContext } from '@/model/globalcontext.model';
import type { IMyTestVM, ITestPublicVM } from '@/services/admin.service';
import AdminService from '@/services/admin.service';
import dayjs from 'dayjs';
import axios from 'axios';

export default defineComponent({
  name: 'Testing',
  components: {
    IonButton,
    IonCheckbox,
    IonContent,
    IonIcon,
    IonPage,
    IonRadio,
    IonRadioGroup,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      isMobile: globalContext.isMobile,
      chevronBackOutline,
      checkmarkCircle,
      chevronForwardOutline,
      documentTextOutline,
    };
  },
  data() {
    return {
      adminService: new AdminService(),
      headerScrolled: false,
      loading: false,
      myTests: [] as IMyTestVM[],
      currentTest: null as ITestPublicVM | null,
      testLoading: false,
      selectedAnswers: {} as Record<number, number[]>,
      submitting: false,
      attemptStartedAt: null as string | null,
      timerIntervalId: null as ReturnType<typeof setInterval> | null,
      timerTick: 0,
      /** Остаток секунд до открытия по серверному дельта (не Date.now()). */
      availabilityCountdownSec: {} as Record<number, number>,
      availabilityCountdownIntervalId: null as ReturnType<typeof setInterval> | null,
    };
  },
  mounted() {
    (this as { _testingMounted?: boolean })._testingMounted = true;
  },
  beforeUnmount() {
    (this as { _testingMounted?: boolean })._testingMounted = false;
    this.stopTimerInterval();
    this.stopAvailabilityCountdown();
  },
  async ionViewWillEnter() {
    await this.loadMyTests();
  },
  computed: {
    timerElapsed(): string {
      void this.timerTick; // зависимость для пересчёта раз в секунду
      const started = this.attemptStartedAt;
      if (!started) return '00:00';
      const sec = Math.floor((Date.now() - new Date(started).getTime()) / 1000);
      const m = Math.floor(sec / 60);
      const h = Math.floor(m / 60);
      return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    },
    timerRemaining(): string {
      void this.timerTick;
      const limit = this.currentTest?.timeLimitMinutes;
      const started = this.attemptStartedAt;
      if (limit == null || !started) return '';
      const elapsedSec = Math.floor((Date.now() - new Date(started).getTime()) / 1000);
      const leftSec = Math.max(0, limit * 60 - elapsedSec);
      const m = Math.floor(leftSec / 60);
      const h = Math.floor(m / 60);
      return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    },
    timeExpired(): boolean {
      void this.timerTick;
      const limit = this.currentTest?.timeLimitMinutes;
      const started = this.attemptStartedAt;
      if (limit == null || !started) return false;
      const elapsedSec = Math.floor((Date.now() - new Date(started).getTime()) / 1000);
      return elapsedSec >= limit * 60;
    },
  },
  methods: {
    startTimerInterval() {
      if (this.timerIntervalId) {
        clearInterval(this.timerIntervalId);
        this.timerIntervalId = null;
      }
      this.timerIntervalId = setInterval(() => {
        this.timerTick = Date.now();
        this.$forceUpdate();
      }, 1000);
    },
    stopTimerInterval() {
      if (this.timerIntervalId) {
        clearInterval(this.timerIntervalId);
        this.timerIntervalId = null;
      }
    },
    stopAvailabilityCountdown() {
      if (this.availabilityCountdownIntervalId) {
        clearInterval(this.availabilityCountdownIntervalId);
        this.availabilityCountdownIntervalId = null;
      }
    },
    startAvailabilityCountdown() {
      this.stopAvailabilityCountdown();
      const hasPending = Object.values(this.availabilityCountdownSec).some((s) => s > 0);
      if (!hasPending) return;
      this.availabilityCountdownIntervalId = setInterval(() => {
        const next: Record<number, number> = { ...this.availabilityCountdownSec };
        let changed = false;
        for (const key of Object.keys(next)) {
          const id = Number(key);
          if (next[id] > 0) {
            next[id] = next[id] - 1;
            changed = true;
          }
        }
        if (changed) {
          this.availabilityCountdownSec = next;
        }
        if (!Object.values(next).some((s) => s > 0)) {
          this.stopAvailabilityCountdown();
        }
      }, 1000);
    },
    syncAvailabilityCountdown(tests: IMyTestVM[]) {
      const next: Record<number, number> = {};
      for (const t of tests) {
        if (t.available || !t.availableFrom || !t.serverNow) {
          next[t.id] = 0;
          continue;
        }
        const fromMs = new Date(t.availableFrom).getTime();
        const nowMs = new Date(t.serverNow).getTime();
        if (Number.isNaN(fromMs) || Number.isNaN(nowMs)) {
          next[t.id] = 0;
          continue;
        }
        next[t.id] = Math.max(0, Math.ceil((fromMs - nowMs) / 1000));
      }
      this.availabilityCountdownSec = next;
      this.startAvailabilityCountdown();
    },
    /** Открыт по серверу или локальный countdown дошёл до нуля. */
    isTestAvailableLocally(t: IMyTestVM): boolean {
      if (t.available) return true;
      if (!t.availableFrom) return true;
      return (this.availabilityCountdownSec[t.id] ?? 0) <= 0;
    },
    canOpenTest(t: IMyTestVM): boolean {
      if (t.passed) return false;
      if (t.hasDraft) return true;
      if (!this.isTestAvailableLocally(t)) return false;
      return !!t.attemptsLeft;
    },
    formatAvailableFrom(iso: string | null | undefined): string {
      if (!iso) return '';
      return dayjs(iso).format('DD.MM.YYYY HH:mm');
    },
    formatCountdown(totalSec: number): string {
      const sec = Math.max(0, totalSec);
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = sec % 60;
      if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      }
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    isTestNotYetAvailableError(e: unknown): boolean {
      const data =
        (axios.isAxiosError(e) ? e.response?.data : null) ||
        (e as { response?: { data?: unknown } } | undefined)?.response?.data ||
        null;
      const blob = JSON.stringify(data || {}) + ' ' + (e instanceof Error ? e.message : String(e || ''));
      return blob.includes('testNotYetAvailable') || blob.includes('ещё не открыт') || blob.includes('еще не открыт');
    },
    onContentScroll(e: Event) {
      const ev = e as CustomEvent;
      this.headerScrolled = (ev.detail?.scrollTop ?? 0) > 30;
    },
    async loadMyTests(event?: CustomEvent) {
      this.loading = true;
      try {
        this.myTests = await this.adminService.getMyTests();
        this.syncAvailabilityCountdown(this.myTests);
      } catch (e) {
        console.error('loadMyTests', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки списка тестов',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.loading = false;
        (event as CustomEvent & { target?: { complete?: () => void } })?.target?.complete?.();
      }
    },
    async onTestCardClick(t: IMyTestVM) {
      if (t.passed) return;
      if (!this.canOpenTest(t)) {
        const toast = await toastController.create({
          message: !this.isTestAvailableLocally(t) && !t.hasDraft
            ? `Тест откроется ${this.formatAvailableFrom(t.availableFrom)}`
            : 'Попытки исчерпаны',
          duration: 2500,
          position: 'top',
          color: 'warning',
        });
        await toast.present();
        return;
      }
      await this.openTest(t);
    },
    async openTest(t: IMyTestVM) {
      if (!t.attemptsLeft && !t.hasDraft) {
        const toast = await toastController.create({
          message: 'Попытки исчерпаны',
          duration: 2000,
          position: 'top',
          color: 'warning',
        });
        await toast.present();
        return;
      }
      if (!this.isTestAvailableLocally(t) && !t.hasDraft) {
        const toast = await toastController.create({
          message: `Тест откроется ${this.formatAvailableFrom(t.availableFrom)}`,
          duration: 2500,
          position: 'top',
          color: 'warning',
        });
        await toast.present();
        return;
      }
      this.testLoading = true;
      this.currentTest = null;
      this.selectedAnswers = {};
      try {
        const test = await this.adminService.getTest(t.id);
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        this.currentTest = test;
        const draft = await this.adminService.getTestDraft(t.id);
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        if (draft?.startedAt) {
          this.attemptStartedAt = draft.startedAt;
          this.$nextTick(() => this.startTimerInterval());
        }
        if (draft?.selectedAnswersByQuestion) {
          const normalized: Record<number, number[]> = {};
          for (const [qId, ans] of Object.entries(draft.selectedAnswersByQuestion)) {
            normalized[Number(qId)] = Array.isArray(ans) ? ans.map(Number) : [];
          }
          this.selectedAnswers = normalized;
        }
      } catch (e) {
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        console.error('openTest', e);
        this.currentTest = null;
        this.attemptStartedAt = null;
        this.stopTimerInterval();
        const notYet = this.isTestNotYetAvailableError(e);
        const toast = await toastController.create({
          message: notYet
            ? `Тест откроется ${this.formatAvailableFrom(t.availableFrom)}`
            : 'Ошибка загрузки теста',
          duration: 3000,
          position: 'top',
          color: notYet ? 'warning' : 'danger',
        });
        await toast.present();
        if (notYet) {
          await this.loadMyTests();
        }
      } finally {
        if ((this as { _testingMounted?: boolean })._testingMounted) {
          this.testLoading = false;
        }
      }
    },
    closeTest() {
      this.currentTest = null;
      this.selectedAnswers = {};
      this.attemptStartedAt = null;
      this.stopTimerInterval();
      this.$nextTick(() => {
        this.loadMyTests();
      });
    },
    isSelected(questionId: number, answerId: number): boolean {
      const arr = this.selectedAnswers[questionId];
      return Array.isArray(arr) && arr.includes(answerId);
    },
    setAnswerChecked(questionId: number, answerId: number, checked: boolean) {
      const arr = this.selectedAnswers[questionId] || [];
      const has = arr.includes(answerId);
      if (checked && !has) {
        this.selectedAnswers = {
          ...this.selectedAnswers,
          [questionId]: [...arr, answerId],
        };
      } else if (!checked && has) {
        this.selectedAnswers = {
          ...this.selectedAnswers,
          [questionId]: arr.filter((id) => id !== answerId),
        };
      }
    },
    toggleAnswer(questionId: number, answerId: number) {
      this.setAnswerChecked(questionId, answerId, !this.isSelected(questionId, answerId));
    },
    onCheckboxChange(questionId: number, answerId: number, event: CustomEvent) {
      this.setAnswerChecked(questionId, answerId, !!event.detail?.checked);
    },
    onSingleChange(questionId: number, event: CustomEvent) {
      const value = event.detail?.value;
      if (value != null) this.selectSingleAnswer(questionId, Number(value));
    },
    selectSingleAnswer(questionId: number, answerId: number) {
      this.selectedAnswers = {
        ...this.selectedAnswers,
        [questionId]: [answerId],
      };
    },
    unansweredQuestionCount(): number {
      if (!this.currentTest?.questions?.length) return 0;
      return this.currentTest.questions.filter((q) => {
        const arr = this.selectedAnswers[q.id];
        return !Array.isArray(arr) || arr.length === 0;
      }).length;
    },
    async submitTest() {
      if (!this.currentTest) return;
      const unanswered = this.unansweredQuestionCount();
      if (unanswered > 0) {
        const toast = await toastController.create({
          message:
            unanswered === 1
              ? 'Ответьте на все вопросы перед завершением теста'
              : `Не отвечено вопросов: ${unanswered}. Ответьте на все перед завершением.`,
          duration: 3500,
          position: 'top',
          color: 'warning',
        });
        await toast.present();
        return;
      }
      this.submitting = true;
      const SUBMIT_TIMEOUT_MS = 25000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), SUBMIT_TIMEOUT_MS);
      });
      try {
        const result = await Promise.race([
          this.adminService.submitTest(this.currentTest.id, this.selectedAnswers),
          timeoutPromise,
        ]);
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        const toast = await toastController.create({
          message: result.passed
            ? `Тест пройден! Результат: ${result.score}%`
            : `Результат: ${result.score}%. Порог: ${this.currentTest.passingThreshold}%`,
          duration: 4000,
          position: 'top',
          color: result.passed ? 'success' : 'warning',
        });
        await toast.present();
        this.closeTest();
      } catch (e) {
        if (!(this as { _testingMounted?: boolean })._testingMounted) return;
        console.error('submitTest', e);
        const isTimeout = e instanceof Error && e.message === 'TIMEOUT';
        const toast = await toastController.create({
          message: isTimeout ? 'Превышено время ожидания. Проверьте интернет и попробуйте снова.' : 'Ошибка отправки',
          duration: 4000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        if ((this as { _testingMounted?: boolean })._testingMounted) {
          this.submitting = false;
        }
      }
    },
  },
});
</script>

<style scoped>
.testing-content {
  --background: var(--ion-background-color);
}

.testing-list-wrap,
.testing-take-wrap {
  padding: 16px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
}

.testing-hint {
  color: var(--ion-text-color);
  opacity: 0.8;
  text-align: center;
  padding: 24px;
}

.testing-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.testing-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: transparent;
  border-radius: 12px;
  border: 1px solid var(--ion-border-color, rgba(255, 255, 255, 0.06));
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s;
}

.testing-card:hover {
  transform: translateY(-2px);
  border-color: rgba(232, 93, 4, 0.3);
}

.testing-card--passed,
.testing-card--locked {
  cursor: default;
  opacity: 0.9;
}

.testing-card--passed:hover,
.testing-card--locked:hover {
  transform: none;
  border-color: var(--ion-border-color, rgba(255, 255, 255, 0.06));
}

.testing-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: transparent;
  color: var(--ion-color-orange);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.testing-card-icon ion-icon {
  font-size: 28px;
}

.testing-card-body {
  flex: 1;
  min-width: 0;
}

.testing-card-title {
  margin: 0 0 4px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ion-text-color);
}

.testing-card-desc {
  margin: 0 0 8px;
  font-size: 0.85rem;
  color: var(--ion-text-color);
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.testing-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
}

.testing-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.testing-status ion-icon {
  font-size: 0.9rem;
}

.testing-status--passed {
  background: var(--ion-color-success);
  color: var(--liga-chrome-text);
}

.testing-status--draft {
  background: var(--ion-color-warning);
  color: var(--ion-color-warning-contrast);
}

.testing-status--open {
  background: var(--ion-color-orange);
  color: var(--liga-chrome-text);
}

.testing-status--scheduled {
  background: rgba(100, 116, 139, 0.85);
  color: #fff;
}

.testing-status--used {
  background: var(--ion-color-medium);
  color: var(--ion-color-medium-contrast);
}

.testing-score {
  color: var(--ion-text-color);
  opacity: 0.8;
}

.testing-card-arrow {
  color: var(--ion-color-medium);
  font-size: 20px;
}

.testing-take-sticky {
  flex-shrink: 0;
  z-index: 10;
  padding: calc(4px + env(safe-area-inset-top, 0px)) 0 12px;
  background: transparent;
}

.testing-take-float-bar {
  padding-left: 10px;
  padding-right: 10px;
}

.testing-take-float-bar .liga-float-circle {
  appearance: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
}

.testing-take-float-bar .liga-float-circle ion-icon {
  font-size: 22px;
  color: var(--liga-chrome-text);
}

.testing-take-header {
  margin: 0 12px;
  padding: 10px 14px;
  border-radius: 16px;
  background: var(--liga-float-glass-bg-strong, rgba(28, 28, 32, 0.72));
  border: 1px solid var(--liga-float-glass-border, rgba(255, 255, 255, 0.14));
  backdrop-filter: var(--liga-float-glass-blur, blur(18px));
  -webkit-backdrop-filter: var(--liga-float-glass-blur, blur(18px));
  box-shadow: var(--liga-float-glass-shadow, 0 8px 28px rgba(0, 0, 0, 0.28));
}

.testing-take-desc {
  margin: 0;
  font-size: 0.9rem;
  color: var(--ion-text-color);
  opacity: 0.85;
}

.testing-timer-wrap {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
  color: var(--ion-text-color);
  opacity: 0.9;
}

.testing-timer-label {
  font-weight: 600;
}

.testing-timer-remaining {
  color: var(--ion-color-orange);
}

.testing-take-loading {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.testing-question-block {
  margin-bottom: 24px;
  padding: 16px;
  background: transparent;
  border-radius: 12px;
  border: 1px solid var(--ion-border-color, rgba(255, 255, 255, 0.06));
}

.testing-question-text {
  margin: 0 0 12px;
  font-weight: 600;
  color: var(--ion-text-color);
}

.testing-answers {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.testing-answer-item {
  --background: transparent;
  --background-hover: transparent;
  --background-activated: transparent;
  --background-focused: transparent;
  --padding-start: 0;
  --inner-padding-end: 0;
  color: var(--ion-text-color);
}

/* Вариант с чекбоксами и радио: контроль слева, текст сразу справа */
.testing-answer-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  cursor: pointer;
  color: var(--ion-text-color);
}
.testing-answer-row ion-checkbox,
.testing-answer-row ion-radio {
  flex-shrink: 0;
}
.testing-answer-row .testing-answer-label {
  flex: 1;
  min-width: 0;
  text-align: start;
}

.testing-take-actions {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--ion-border-color);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.testing-take-actions ion-button {
  --border-radius: 12px;
  font-weight: 600;
  min-height: 48px;
}

/* Кнопки в стиле приложения: чёрно-оранжевые */
.testing-take-actions ion-button[fill="outline"] {
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --color-hover: var(--ion-color-orange-tint);
  --color-activated: var(--ion-color-orange-tint);
}

/* Кнопка «Завершить тест» — как «Создать тест» в админке (admin-primary-btn) */
.testing-take-actions ion-button.admin-primary-btn,
.testing-take-actions ion-button.admin-primary-btn.ion-activated,
.testing-take-actions ion-button.admin-primary-btn.ion-focused {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --background-focused: var(--ion-color-orange-tint);
  --color: var(--liga-on-accent);
}

/* Чекбоксы: светлый фон при отмеченном, оранжевая рамка и галочка — не сливаются с фоном */
.testing-answers ion-checkbox {
  --size: 22px;
  --checkbox-background: transparent;
  --checkbox-background-checked: rgba(255, 255, 255, 0.95);
  --checkmark-color: var(--ion-color-orange);
  --border-color: var(--ion-color-medium);
  --border-color-checked: var(--ion-color-orange);
  --border-width: 2px;
}

/* Радио: видимый пустой круг (тёмная обводка), оранжевый при выборе, размер как у чекбоксов */
.testing-answers ion-radio {
  --color: var(--ion-color-step-600, #666);
  --color-checked: var(--ion-color-orange);
  --inner-width: 10px;
  --inner-height: 10px;
  --outer-width: 22px;
  --outer-height: 22px;
  --border-width: 2px;
  --border-color: var(--ion-color-step-600, #666);
  --border-color-checked: var(--ion-color-orange);
}
.testing-answers ion-radio::part(container) {
  width: 22px;
  height: 22px;
  border: 2px solid var(--ion-color-step-600, #666);
  border-radius: 50%;
  background: transparent;
}
.testing-answers ion-radio.ion-checked::part(container) {
  border-color: var(--ion-color-orange);
  background: transparent;
}
</style>

<!-- Стили без scoped: переопределение кнопок Ionic (переменные пробрасываются в shadow DOM) -->
<style>
/* Кнопка «Завершить тест» — те же стили, что у admin-primary-btn в админке тестов */
ion-content.testing-content .testing-take-actions ion-button.admin-primary-btn {
  --background: var(--ion-color-orange);
  --background-hover: var(--ion-color-orange-tint);
  --background-activated: var(--ion-color-orange-tint);
  --color: var(--liga-on-accent);
}
</style>
